import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  createWorksheet,
  getWorksheetsByNode,
  updateWorksheet,
  deleteWorksheet,
  getAISuggestion
} from '../../services/operations/worksheetAPI';
import toast from 'react-hot-toast';
import { Download, ArrowLeft, Plus, Trash2, X, Sparkles, Loader2, FileText, Brain } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HAZOPWorksheet = () => {
  const { nodeId } = useParams();
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [newEntry, setNewEntry] = useState({
    guideWord: '',
    parameter: '',
    deviation: '',
    causes: '',
    consequences: '',
    severity: 1,
    likelihood: 1,
    risk: 1,
    existingSafeguard: '',
    recommendation: '',
  });
  const [editingCell, setEditingCell] = useState(null);

  const guideWordOptions = ["No", "Less", "More", "As Well As", "Part Of", "Other Than", "Reverse"];
  const parameterOptions = [
    "Containment", "Flow", "Temperature", "Pressure", "Level",
    "Composition", "Mixing", "Weight", "PHRAs", "Services/Utilities",
    "Personnel Safety", "Maintenance", "Reaction"
  ];

  const calculateRisk = (severity, likelihood) => {
    const s = Number(severity);
    const l = Number(likelihood);
    if (!isNaN(s) && !isNaN(l) && s >= 1 && s <= 5 && l >= 1 && l <= 5) {
      return s * l;
    }
    return 0;
  };

  const getRiskColor = (risk) => {
    if (risk >= 17) return 'bg-gradient-to-br from-red-500 to-pink-600 text-white shadow-lg';
    if (risk >= 10) return 'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg';
    if (risk >= 5) return 'bg-gradient-to-br from-yellow-400 to-amber-500 text-slate-800 shadow-lg';
    if (risk >= 1) return 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg';
    return 'bg-gradient-to-br from-slate-200 to-gray-300 text-slate-800 shadow-sm';
  };

  useEffect(() => {
    fetchWorksheets();
  }, [nodeId]);

  const fetchWorksheets = async () => {
    setLoading(true);
    try {
      const response = await getWorksheetsByNode(nodeId);
      const result = response.data;

      if (result) {
        const formattedWorksheets = result.map(ws => {
          const severity = Number(ws.severity) || 1;
          const likelihood = Number(ws.likelihood) || 1;
          const risk = calculateRisk(severity, likelihood);
          
          let guideWord = '';
          let parameter = '';
          if (ws.guideWordParameter && typeof ws.guideWordParameter === 'string') {
            const parts = ws.guideWordParameter.split('+');
            guideWord = parts[0] || '';
            parameter = parts[1] || '';
          }

          return {
            ...ws,
            guideWord,
            parameter,
            causes: Array.isArray(ws.causes) ? ws.causes.join(', ') : ws.causes || '',
            consequences: Array.isArray(ws.consequences) ? ws.consequences.join(', ') : ws.consequences || '',
            existingSafeguard: Array.isArray(ws.existingSafeguard) ? ws.existingSafeguard.join(', ') : ws.existingSafeguard || '',
            recommendation: Array.isArray(ws.recommendation) ? ws.recommendation.join(', ') : ws.recommendation || '',
            severity,
            likelihood,
            risk,
          };
        });
        setWorksheets(formattedWorksheets);
      }
    } catch (error) {
      console.error("Error fetching worksheets:", error);
      toast.error("Failed to load HAZOP worksheets.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, index, field) => {
    const value = e.target.value;
    let updatedEntry;

    if (index === 'new') {
      updatedEntry = { ...newEntry, [field]: value };
      if (field === 'severity' || field === 'likelihood') {
        const s = field === 'severity' ? Number(value) : newEntry.severity;
        const l = field === 'likelihood' ? Number(value) : newEntry.likelihood;
        updatedEntry.risk = calculateRisk(s, l);
      }
      setNewEntry(updatedEntry);
    } else {
      const updatedWorksheets = [...worksheets];
      updatedEntry = { ...updatedWorksheets[index], [field]: value };
      if (field === 'severity' || field === 'likelihood') {
        const s = field === 'severity' ? Number(value) : updatedEntry.severity;
        const l = field === 'likelihood' ? Number(value) : updatedEntry.likelihood;
        updatedEntry.risk = calculateRisk(s, l);
      }
      updatedWorksheets[index] = updatedEntry;
      setWorksheets(updatedWorksheets);
    }
  };

  const formatArrayFieldsForBackend = (entry) => ({
    ...entry,
    causes: entry.causes.split(',').map(s => s.trim()).filter(s => s),
    consequences: entry.consequences.split(',').map(s => s.trim()).filter(s => s),
    existingSafeguard: entry.existingSafeguard.split(',').map(s => s.trim()).filter(s => s),
    recommendation: entry.recommendation.split(',').map(s => s.trim()).filter(s => s),
  });

  const handleCreateWorksheet = async () => {
    if (!newEntry.guideWord || !newEntry.parameter) {
      toast.error("Guide Word and Parameter are required for a new entry.");
      return;
    }
    const toastId = toast.loading("Creating new entry...");
    try {
      const dataToSend = {
        ...formatArrayFieldsForBackend(newEntry),
        guideWordParameter: `${newEntry.guideWord}+${newEntry.parameter}`,
        nodeId: nodeId, 
        severity: Number(newEntry.severity),
        likelihood: Number(newEntry.likelihood),
      };

      delete dataToSend.guideWord;
      delete dataToSend.parameter;
      
      const response = await createWorksheet(dataToSend);
      
      const result = response.worksheet;
      if (response.success) {
        fetchWorksheets();
      }

      if (result) {
        const severity = Number(result.severity) || 1;
        const likelihood = Number(result.likelihood) || 1;
        const risk = calculateRisk(severity, likelihood);
        const formattedResult = {
          ...result,
          causes: Array.isArray(result.causes) ? result.causes.join(', ') : result.causes || '',
          consequences: Array.isArray(result.consequences) ? result.consequences.join(', ') : result.consequences || '',
          existingSafeguard: Array.isArray(result.existingSafeguard) ? result.existingSafeguard.join(', ') : result.existingSafeguard || '',
          recommendation: Array.isArray(result.recommendation) ? result.recommendation.join(', ') : result.recommendation || '',
          severity,
          likelihood,
          risk,
        };
        setWorksheets([...worksheets, formattedResult]);
        setNewEntry({
          guideWord: '',
          parameter: '',
          deviation: '',
          causes: '',
          consequences: '',
          severity: 1,
          likelihood: 1,
          risk: 1,
          existingSafeguard: '',
          recommendation: '',
        });
      }
    } catch (error) {
      console.error("Error creating worksheet:", error);
    } finally {
      toast.dismiss(toastId);
    }
  };

  const handleUpdateWorksheet = async (worksheetId, updatedData) => {
    const toastId = toast.loading("Updating entry...");
    try {
      const dataToSend = {
        ...formatArrayFieldsForBackend(updatedData),
        severity: Number(updatedData.severity),
        likelihood: Number(updatedData.likelihood),
      };

      const res = await updateWorksheet(worksheetId, dataToSend);
      const result = res.worksheet;
      if (result) {
        const severity = Number(result.severity) || 1;
        const likelihood = Number(result.likelihood) || 1;
        const risk = calculateRisk(severity, likelihood);
        const formattedResult = {
          ...result,
          causes: Array.isArray(result.causes) ? result.causes.join(', ') : result.causes || '',
          consequences: Array.isArray(result.consequences) ? result.consequences.join(', ') : result.consequences || '',
          existingSafeguard: Array.isArray(result.existingSafeguard) ? result.existingSafeguard.join(', ') : result.existingSafeguard || '',
          recommendation: Array.isArray(result.recommendation) ? result.recommendation.join(', ') : result.recommendation || '',
          severity,
          likelihood,
          risk,
        };
        setWorksheets(worksheets.map(ws => ws._id === worksheetId ? formattedResult : ws));
        toast.success("Entry updated successfully!");
      }
    } catch (error) {
      console.error("Error updating worksheet:", error);
      toast.error("Failed to update entry.");
    } finally {
      toast.dismiss(toastId);
    }
  };

  const handleDeleteWorksheet = async (worksheetId) => {
    const toastId = toast.loading("Deleting entry...");
    try {
      await deleteWorksheet(worksheetId);
      setWorksheets(worksheets.filter(ws => ws._id !== worksheetId));
      toast.success("Entry deleted successfully!");
    } catch (error) {
      console.error("Error deleting worksheet:", error);
      toast.error("Failed to delete entry.");
    } finally {
      toast.dismiss(toastId);
    }
  };

  const handleGetAISuggestion = async (index, guideWord, parameter) => {
    if (!guideWord || !parameter) {
      toast.error("Guide Word and Parameter are required for AI suggestion.");
      return;
    }

    const toastId = toast.loading("Getting AI suggestion...");
    try {
      const aiSuggestions = await getAISuggestion(guideWord, parameter, ""); 

      if (aiSuggestions) {
        let targetEntry;
        if (index === 'new') {
          targetEntry = { ...newEntry };
        } else {
          targetEntry = { ...worksheets[index] };
        }

        targetEntry.deviation = aiSuggestions.deviation || targetEntry.deviation;
        targetEntry.causes = Array.isArray(aiSuggestions.causes) ? aiSuggestions.causes.join(', ') : aiSuggestions.causes || targetEntry.causes;
        targetEntry.consequences = Array.isArray(aiSuggestions.consequences) ? aiSuggestions.consequences.join(', ') : aiSuggestions.consequences || targetEntry.consequences;

        const aiSeverity = Number(aiSuggestions.severity);
        targetEntry.severity = !isNaN(aiSeverity) && aiSeverity >=1 && aiSeverity <=5 ? aiSeverity : targetEntry.severity;

        const aiLikelihood = Number(aiSuggestions.likelihood);
        targetEntry.likelihood = !isNaN(aiLikelihood) && aiLikelihood >=1 && aiLikelihood <=5 ? aiLikelihood : targetEntry.likelihood;

        targetEntry.risk = calculateRisk(targetEntry.severity, targetEntry.likelihood);

        targetEntry.existingSafeguard = Array.isArray(aiSuggestions.existingSafeguard) ? aiSuggestions.existingSafeguard.join(', ') : aiSuggestions.existingSafeguard || aiSuggestions.existingSafeguards?.join(', ') || targetEntry.existingSafeguard;
        targetEntry.recommendation = Array.isArray(aiSuggestions.recommendation) ? aiSuggestions.recommendation.join(', ') : aiSuggestions.recommendation || targetEntry.recommendation;

        if (index === 'new') {
          setNewEntry(targetEntry);
        } else {
          const updatedWorksheets = [...worksheets];
          updatedWorksheets[index] = targetEntry;
          setWorksheets(updatedWorksheets);
        }
        toast.success("AI suggestion applied!");
      } else {
        toast.error("AI could not provide suggestions.");
      }
    } catch (error) {
      console.error("Error getting AI suggestion:", error);
      toast.error("Failed to get AI suggestion.");
    } finally {
      toast.dismiss(toastId);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 text-blue-500 mx-auto mb-4" />
          <p className="text-slate-600 text-lg">Loading HAZOP Worksheet...</p>
        </div>
      </div>
    );
  }

  const renderBulletPointsOrInput = (value, rowIndex, fieldName) => {
    const isCurrentlyEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === fieldName;
    const items = value.split(',').map(item => item.trim()).filter(item => item !== '');

    return (
      <div className="min-h-[4rem] flex items-center">
        {isCurrentlyEditing ? (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(e, rowIndex, fieldName)}
            onBlur={() => {
              if (rowIndex !== 'new') {
                handleUpdateWorksheet(worksheets[rowIndex]._id, worksheets[rowIndex]);
              }
              setEditingCell(null);
            }}
            className="w-full bg-white/80 backdrop-blur-sm border border-blue-200 rounded-lg shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:ring-opacity-50 text-sm p-3 transition-all duration-300"
            style={{ wordBreak: 'break-word', overflowY: 'auto' }}
            rows="2"
            autoFocus
          />
        ) : (
          <div
            onClick={() => setEditingCell({ rowIndex, field: fieldName })}
            className="cursor-pointer w-full h-full p-3 rounded-lg hover:bg-white/50 transition-all duration-200 border border-transparent hover:border-blue-200"
          >
            {items.length > 0 ? (
              <ul className="list-disc list-inside text-sm text-slate-700 space-y-1" style={{ wordBreak: 'break-word' }}>
                {items.map((item, i) => (
                  <li key={i} className="leading-relaxed">{item}</li>
                ))}
              </ul>
            ) : (
              <span className="text-slate-400 italic">Click to add/edit...</span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Modern Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
          
          
          <div className="text-center">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
                <FileText className="h-6 w-6 text-white" />
              </div>
            
            <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              HAZOP Worksheet Management
            </h1>
            </div>
            <p className="text-slate-600">Create and manage your HAZOP analysis worksheets</p>
          </div>
          
          <button
            onClick={() => navigate(`/dashboard/download/${nodeId}`)}
            className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl p-3 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            <Download size={24} />
          </button>
        </div>

        {/* Worksheet Tables Container */}
        <div className="space-y-8">
          {/* New Entry Form */}
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/5 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="p-6 border-b border-white/20 bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                    <Plus className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-800">Create New Entry</h2>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full table-fixed">
                  <thead className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider rounded-tl-xl" style={{ minWidth: '120px', maxWidth: '140px' }}>Guide Word</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '0px', maxWidth: '140px' }}>Parameter</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '0px', maxWidth: '200px' }}>Deviation</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '0px', maxWidth: '200px' }}>Causes</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '0px', maxWidth: '200px' }}>Consequences</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '80px', maxWidth: '100px' }}>Severity (1-5)</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '80px', maxWidth: '100px' }}>Likelihood (1-5)</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '70px', maxWidth: '90px' }}>Risk</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '0px', maxWidth: '200px' }}>Existing Safeguard</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '0px', maxWidth: '200px' }}>Recommendation</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider rounded-tr-xl" style={{ minWidth: '120px', maxWidth: '140px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white/50 backdrop-blur-sm">
                    <tr className="hover:bg-white/70 transition-colors duration-200">
                      <td className="px-2 py-2">
                        <select
                          value={newEntry.guideWord}
                          onChange={(e) => handleInputChange(e, 'new', 'guideWord')}
                          className="w-full bg-white/80 backdrop-blur-sm border border-black/30 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300"
                        >
                          <option value="">Select Guide Word</option>
                          {guideWordOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <select
                          value={newEntry.parameter}
                          onChange={(e) => handleInputChange(e, 'new', 'parameter')}
                          className="w-full bg-white/80 backdrop-blur-sm border border-black/30 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300"
                        >
                          <option value="">Select Parameter</option>
                          {parameterOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <textarea
                          value={newEntry.deviation}
                          onChange={(e) => handleInputChange(e, 'new', 'deviation')}
                          className="w-full bg-white/80 backdrop-blur-sm border border-black/30 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300 text-sm resize-none"
                          rows="2"
                          placeholder="Enter deviation"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <textarea
                          value={newEntry.causes}
                          onChange={(e) => handleInputChange(e, 'new', 'causes')}
                          className="w-full bg-white/80 backdrop-blur-sm border border-black/30 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300 text-sm resize-none"
                          rows="2"
                          placeholder="Comma-separated values"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <textarea
                          value={newEntry.consequences}
                          onChange={(e) => handleInputChange(e, 'new', 'consequences')}
                          className="w-full bg-white/80 backdrop-blur-sm border border-black/30 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300 text-sm resize-none"
                          rows="2"
                          placeholder="Comma-separated values"
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="number"
                          value={newEntry.severity}
                          onChange={(e) => handleInputChange(e, 'new', 'severity')}
                          className="w-full bg-white/80 backdrop-blur-sm border border-black/30 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300 text-center"
                          min="1"
                          max="5"
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="number"
                          value={newEntry.likelihood}
                          onChange={(e) => handleInputChange(e, 'new', 'likelihood')}
                          className="w-full bg-white/80 backdrop-blur-sm border border-black/30 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300 text-center"
                          min="1"
                          max="5"
                        />
                      </td>
                      <td className={`px-2 py-2 text-center font-bold rounded-lg ${getRiskColor(newEntry.risk)}`}>
                        {newEntry.risk}
                      </td>
                      <td className="px-2 py-2">
                        <textarea
                          value={newEntry.existingSafeguard}
                          onChange={(e) => handleInputChange(e, 'new', 'existingSafeguard')}
                          className="w-full bg-white/80 backdrop-blur-sm border border-black/30 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300 text-sm resize-none"
                          rows="2"
                          placeholder="Comma-separated values"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <textarea
                          value={newEntry.recommendation}
                          onChange={(e) => handleInputChange(e, 'new', 'recommendation')}
                          className="w-full bg-white/80 backdrop-blur-sm border border-black/30 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300 text-sm resize-none"
                          rows="2"
                          placeholder="Comma-separated values"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex flex-col space-y-2">
                          <button
                            onClick={handleCreateWorksheet}
                            className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white py-2 px-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-1 transform hover:scale-105 font-semibold text-sm"
                          >
                            <Plus className="h-4 w-4" />
                            Create
                          </button>
                          <button
                            onClick={() => handleGetAISuggestion('new', newEntry.guideWord, newEntry.parameter)}
                            className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white py-2 px-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-1 transform hover:scale-105 font-semibold text-sm"
                          >
                            <Brain className="h-4 w-4" />
                            AI
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Existing Entries */}
          {worksheets.map((worksheet, index) => (
            <div key={worksheet._id} className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-500/5 to-gray-600/5 rounded-2xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
                <div className="p-4 border-b border-white/20 bg-gradient-to-r from-slate-50/50 to-white/50">
                  <h3 className="text-lg font-semibold text-slate-800">Entry #{index + 1}</h3>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full table-fixed">
                    <thead className="bg-gradient-to-r from-slate-500 to-gray-600 text-white">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider rounded-tl-xl" style={{ minWidth: '120px', maxWidth: '140px' }}>Guide Word</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '0', maxWidth: '140px' }}>Parameter</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '0', maxWidth: '200px' }}>Deviation</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '150px', maxWidth: '200px' }}>Causes</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '0', maxWidth: '200px' }}>Consequences</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '80px', maxWidth: '100px' }}>Severity (1-5)</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '80px', maxWidth: '100px' }}>Likelihood (1-5)</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '70px', maxWidth: '90px' }}>Risk</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '0', maxWidth: '200px' }}>Existing Safeguard</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ minWidth: '0', maxWidth: '200px' }}>Recommendation</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider rounded-tr-xl" style={{ minWidth: '120px', maxWidth: '140px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white/50 backdrop-blur-sm">
                      <tr className="hover:bg-white/70 transition-colors duration-200">
                        <td className="px-4 py-3">
                          <select
                            value={worksheet.guideWord}
                            onChange={(e) => handleInputChange(e, index, 'guideWord')}
                            onBlur={() => handleUpdateWorksheet(worksheet._id, worksheet)}
                            className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300"
                          >
                            {guideWordOptions.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={worksheet.parameter}
                            onChange={(e) => handleInputChange(e, index, 'parameter')}
                            onBlur={() => handleUpdateWorksheet(worksheet._id, worksheet)}
                            className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300"
                          >
                            {parameterOptions.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <textarea
                            value={worksheet.deviation}
                            onChange={(e) => handleInputChange(e, index, 'deviation')}
                            onBlur={() => handleUpdateWorksheet(worksheet._id, worksheet)}
                            className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300 text-sm resize-none"
                            rows="2"
                          />
                        </td>
                        <td className="px-4 py-3">
                          {renderBulletPointsOrInput(worksheet.causes, index, 'causes')}
                        </td>
                        <td className="px-4 py-3">
                          {renderBulletPointsOrInput(worksheet.consequences, index, 'consequences')}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="number"
                            value={worksheet.severity}
                            onChange={(e) => handleInputChange(e, index, 'severity')}
                            onBlur={() => handleUpdateWorksheet(worksheet._id, worksheet)}
                            className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300 text-center"
                            min="1"
                            max="5"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="number"
                            value={worksheet.likelihood}
                            onChange={(e) => handleInputChange(e, index, 'likelihood')}
                            onBlur={() => handleUpdateWorksheet(worksheet._id, worksheet)}
                            className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-sm transition-all duration-300 text-center"
                            min="1"
                            max="5"
                          />
                        </td>
                        <td className={`px-4 py-3 text-center font-bold rounded-lg ${getRiskColor(worksheet.risk)}`}>
                          {worksheet.risk}
                        </td>
                        <td className="px-4 py-3">
                          {renderBulletPointsOrInput(worksheet.existingSafeguard, index, 'existingSafeguard')}
                        </td>
                        <td className="px-4 py-3">
                          {renderBulletPointsOrInput(worksheet.recommendation, index, 'recommendation')}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col space-y-2">
                            <button
                              onClick={() => handleDeleteWorksheet(worksheet._id)}
                              className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white p-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center transform hover:scale-105"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HAZOPWorksheet;